import { supabase } from '@/integrations/supabase/client';

// ==================== INTERFACES ====================

export type TtsProvider = 'unrealspeech' | 'sarvam' | 'deepgram';

export interface TtsOptions {
  provider?: TtsProvider;
  voiceId?: string;       // UnrealSpeech voice or Sarvam speaker
  speaker?: string;       // Sarvam speaker parameter backup
  speed?: number;         // UnrealSpeech speed or Sarvam pace
  pitch?: number;         // UnrealSpeech pitch
  languageCode?: string;  // Sarvam target language (e.g., 'en-IN', 'hi-IN')
  pace?: number;          // Sarvam pace/speed override
  endpoint?: string;      // UnrealSpeech endpoint: 'stream' | 'synthesisTasks' | 'speech'
  timestampType?: string; // UnrealSpeech timestamp type
  bitrate?: string;       // UnrealSpeech bitrate (e.g., '48k', '64k')
  modelId?: string;       // Deepgram voice model name
  signal?: AbortSignal;
}

// ==================== EDGE FUNCTION INTEGRATION ====================

async function callEdgeFunction(text: string, options: TtsOptions): Promise<any> {
  try {
    const { data, error } = await supabase.functions.invoke('text-to-speech', {
      body: { text, ...options },
      signal: options.signal,
    } as any);

    if (error) {
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Edge function execution failed');
    }
    return data;
  } catch (error) {
    console.error('Supabase TTS edge function call failed:', error);
    throw error;
  }
}

// ==================== UTILS ====================

const blobToAudio = async (blob: Blob): Promise<HTMLAudioElement> => {
  const audioUrl = URL.createObjectURL(blob);
  const audio = new Audio(audioUrl);

  return new Promise((resolve, reject) => {
    audio.onloadeddata = () => resolve(audio);
    audio.onerror = () => {
      URL.revokeObjectURL(audioUrl);
      reject(new Error('Failed to load generated audio'));
    };
  });
};

// ==================== EXPORTED UNIFIED API ====================

/**
 * High-level unified function that returns an HTMLAudioElement.
 * Requests audio from the serverless edge function.
 */
export async function generateSpeechStream(text: string, options: TtsOptions = {}): Promise<HTMLAudioElement> {
  const provider = options.provider || 'unrealspeech';

  const data = await callEdgeFunction(text, options);
  if (data.audioContent) {
    // Audio content returned as base64
    const binary = atob(data.audioContent);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    const mime = provider === 'sarvam' ? 'audio/wav' : 'audio/mp3';
    const blob = new Blob([array], { type: mime });
    return await blobToAudio(blob);
  } else if (data.OutputUri || data.audioUrl) {
    const url = data.OutputUri || data.audioUrl;
    const audio = new Audio(url);
    return new Promise((resolve, reject) => {
      audio.onloadeddata = () => resolve(audio);
      audio.onerror = () => reject(new Error('Failed to load edge function audio URL'));
    });
  }

  throw new Error('No audio content returned from TTS service');
}

/**
 * Standard generateTextToSpeech function to maintain backward compatibility with UnrealSpeech usages.
 */
export async function generateTextToSpeech(options: TtsOptions & { text: string }): Promise<{
  success: boolean;
  audioContent?: string;
  OutputUri?: string;
  TimestampsUri?: string;
  error?: string;
}> {
  const { text, ...rest } = options;
  const requestOptions: TtsOptions = { provider: 'unrealspeech', ...rest };

  try {
    const data = await callEdgeFunction(text, requestOptions);
    return {
      success: true,
      audioContent: data.audioContent,
      OutputUri: data.OutputUri || data.audioUrl,
      TimestampsUri: data.TimestampsUri || data.timestampsUrl
    };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Failed to generate speech'
    };
  }
}

/**
 * Standard generateSpeechWithTimestamps function for UnrealSpeech timestamp features.
 */
export async function generateSpeechWithTimestamps(options: TtsOptions & { text: string }): Promise<{
  audioUrl: string;
  timestampsUrl: string;
}> {
  const { text, ...rest } = options;
  const requestOptions: TtsOptions = { provider: 'unrealspeech', endpoint: 'speech', ...rest };

  const data = await callEdgeFunction(text, requestOptions);
  return {
    audioUrl: data.OutputUri || data.audioUrl || '',
    timestampsUrl: data.TimestampsUri || data.timestampsUrl || ''
  };
}

/**
 * Standard generateSarvamSpeech function to maintain backward compatibility with Sarvam AI usages.
 */
export async function generateSarvamSpeech(options: TtsOptions & { text: string }): Promise<{
  success: boolean;
  audioContent?: string;
  error?: string;
}> {
  const { text, ...rest } = options;
  const requestOptions: TtsOptions = { provider: 'sarvam', ...rest };

  try {
    const data = await callEdgeFunction(text, requestOptions);
    return {
      success: true,
      audioContent: data.audioContent
    };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Failed to generate Sarvam speech'
    };
  }
}
